import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { useSettings } from 'hooks';
import type { Settings } from 'features/general/types';
import { getPotentialPaymentMethods } from 'features/paymentMethods/api';
import useVisiblePaymentMethods from 'ui/organizms/PaymentMethods/VisiblePaymentMethods/useVisiblePaymentMethods';

type TransactionForm = {
    tag: string;
    currency: string;
    amount: string;
    countrycode: string;
    trackingmembercode: string;
    transaction_count: string;
    merchantredirecturl: string;
    notificationurl: string;
    source_url: string;
    source_ip: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    phone_verified: string;
    address: string;
    zip: string;
    city: string;
    country: string;
    nationality: string;
    specin: string;
};

const initialForm: TransactionForm = {
    tag: '', currency: 'EUR', amount: '10.00', countrycode: '',
    trackingmembercode: `docs-${Date.now()}`, transaction_count: '',
    merchantredirecturl: 'https://example.com/payment-result',
    notificationurl: 'https://example.com/payment-notification',
    source_url: 'https://example.com', source_ip: '', first_name: '', last_name: '',
    email: '', phone: '', phone_verified: '1', address: '', zip: '', city: '', country: '', nationality: '',
    specin: ''
};

const requiredFields: ReadonlyArray<keyof TransactionForm> = [
    'tag', 'currency', 'amount', 'countrycode', 'trackingmembercode', 'merchantredirecturl',
    'notificationurl', 'first_name', 'last_name', 'email'
];

const hiddenFields: ReadonlyArray<keyof TransactionForm> = [
    'source_url',
    'source_ip',
    'phone_verified'
];

const fieldLabels: Record<keyof TransactionForm, string> = {
    tag: 'Payment method tag', currency: 'Currency', amount: 'Amount', countrycode: 'Transaction country',
    trackingmembercode: 'Tracking member code', transaction_count: 'Transaction count', merchantredirecturl: 'Merchant redirect URL',
    notificationurl: 'Notification URL', source_url: 'Source URL', source_ip: 'Consumer IP address',
    first_name: 'First name', last_name: 'Last name', email: 'Email', phone: 'Phone',
    phone_verified: 'Phone verified (1 or 0)', address: 'Address', zip: 'ZIP / postal code',
    city: 'City', country: 'Customer country', nationality: 'Nationality', specin: 'Specin'
};

type ComposedPaymentMethod = {
    readonly tag: string | number;
    readonly forCountries: string | null;
    readonly forCurrencies: string | null;
    readonly pki: null;
};

type SelectedMethodDetails = {
    readonly tag: string;
    readonly countries: readonly string[];
    readonly currencies: readonly string[];
};

const splitOptions = (value: string | readonly string[] | null | undefined) => Array.from(new Set(
    (Array.isArray(value) ? value : String(value ?? '').split(','))
        .map(option => option.trim())
        .filter(Boolean)
));

const getResources = (payload: any): ComposedPaymentMethod[] => {
    const data = payload?.data?.data ?? payload?.data ?? payload;
    return Array.isArray(data) ? data : [];
};

const sandboxTransactionUrl = `${(process.env.REACT_APP_BACKEND_ORIGIN_URL ?? '')
    .replace(/\/$/, '')}/sandbox-demo/v1/transaction/submit`;
const isVisibleMethod = (value?: number | string | boolean) =>
    value === true || value === 1 || value === '1';

const findRedirectUrl = (value: unknown): string | null => {
    if (!value || typeof value !== 'object') return null;

    for (const [key, nestedValue] of Object.entries(value)) {
        if (
            key.replace(/[^a-z]/gi, '').toLowerCase() === 'redirecturl' &&
            typeof nestedValue === 'string' &&
            /^https?:\/\//i.test(nestedValue)
        ) {
            return nestedValue;
        }

        const nestedRedirectUrl = findRedirectUrl(nestedValue);
        if (nestedRedirectUrl) return nestedRedirectUrl;
    }

    return null;
};

const TransactionPlayground = () => {
    const { getSettings } = useSettings<Settings>();
    const settings = getSettings();
    const visiblePaymentMethods = useVisiblePaymentMethods();
    const [form, setForm] = useState(initialForm);
    const [isSubmitting, setSubmitting] = useState(false);
    const [httpStatus, setHttpStatus] = useState<number | null>(null);
    const [response, setResponse] = useState<unknown>(null);
    const [error, setError] = useState<string | null>(null);
    const [selectedMethod, setSelectedMethod] = useState<SelectedMethodDetails | null>(null);
    const [methodsError, setMethodsError] = useState<string | null>(null);
    const [areMethodDetailsLoading, setMethodDetailsLoading] = useState(false);
    const hasCredentials = Boolean(settings?.apiId && settings?.apiGuid);

    useEffect(() => {
        let isCurrentRequest = true;
        setSelectedMethod(null);
        if (!form.tag) {
            setMethodDetailsLoading(false);
            setMethodsError(null);
            return () => { isCurrentRequest = false; };
        }

        setMethodDetailsLoading(true);
        setMethodsError(null);

        getPotentialPaymentMethods(`?tag=${encodeURIComponent(form.tag)}&perPage=500`)
            .then(async result => {
                if (!result.ok) throw new Error(`HTTP ${result.status}`);
                const payload = await result.json();
                if (payload?.success === false) {
                    throw new Error(payload.message || `API error ${payload.code ?? 'unknown'}`);
                }

                const matchingResources = getResources(payload).filter(resource =>
                    String(resource.tag ?? '').trim().toLowerCase() === form.tag.toLowerCase()
                );
                const countries = new Set<string>();
                const currencies = new Set<string>();

                matchingResources.forEach(resource => {
                    splitOptions(resource.forCountries)
                        .forEach(country => countries.add(country));
                    splitOptions(resource.forCurrencies)
                        .forEach(currency => currencies.add(currency));
                });

                if (isCurrentRequest) {
                    setSelectedMethod({
                        tag: form.tag,
                        countries: Array.from(countries).sort(),
                        currencies: Array.from(currencies).sort()
                    });
                }
            })
            .catch(requestError => {
                if (isCurrentRequest) {
                    const details = requestError instanceof Error ? requestError.message : 'Unknown error';
                    setMethodsError(`Details for ${form.tag} could not be loaded: ${details}.`);
                }
            })
            .finally(() => {
                if (isCurrentRequest) setMethodDetailsLoading(false);
            });

        return () => { isCurrentRequest = false; };
    }, [form.tag]);

    const assignedMethodOptions = useMemo(() => Array.from(
        new Set<string>(visiblePaymentMethods.getPaymentMethods()
            .filter(method => isVisibleMethod(method.isVisible))
            .map(method => method.tag.trim())
            .filter(Boolean))
    )
        .sort((first, second) => first.localeCompare(second)), [visiblePaymentMethods]);

    const requestPreview = useMemo(() => ({
        api_id: settings?.apiId ? '••••••••••••' : 'Loaded from merchant profile',
        api_guid: settings?.apiGuid ? '••••••••••••' : 'Loaded from merchant profile',
        channel: 'testchannel',
        tag: form.tag, currency: form.currency, amount: form.amount, countrycode: form.countrycode,
        trackingmembercode: form.trackingmembercode, merchantredirecturl: form.merchantredirecturl,
        ...(form.transaction_count && { transaction_count: Number(form.transaction_count) }),
        notificationurl: form.notificationurl,
        ...(form.specin && { specin: form.specin }),
        customer: {
            first_name: form.first_name, last_name: form.last_name, email: form.email,
            ...(form.phone && { phone: form.phone }),
            ...(form.address && { address: form.address }), ...(form.zip && { zip: form.zip }),
            ...(form.city && { city: form.city }), ...(form.country && { country: form.country }),
            ...(form.nationality && { nationality: form.nationality })
        }
    }), [form, settings?.apiGuid, settings?.apiId]);

    const setField = (field: keyof TransactionForm, value: string) =>
        setForm(current => ({ ...current, [field]: value }));

    const setPaymentMethod = (tag: string) => {
        setForm(current => ({
            ...current,
            tag,
            countrycode: '',
            currency: ''
        }));
    };

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setError(null); setResponse(null); setHttpStatus(null);
        if (!hasCredentials) {
            setError('API credentials could not be loaded from the merchant profile.');
            return;
        }

        const body = {
            api_id: Number(settings.apiId), api_guid: settings.apiGuid, channel: 'testchannel',
            tag: form.tag, currency: form.currency, amount: form.amount, countrycode: form.countrycode,
            trackingmembercode: form.trackingmembercode, merchantredirecturl: form.merchantredirecturl,
            ...(form.transaction_count && { transaction_count: Number(form.transaction_count) }),
            notificationurl: form.notificationurl,
            ...(form.specin && { specin: form.specin }),
            customer: {
                first_name: form.first_name,
                last_name: form.last_name,
                email: form.email,
                ...(form.phone && { phone: form.phone }),
                ...(form.address && { address: form.address }),
                ...(form.zip && { zip: form.zip }),
                ...(form.city && { city: form.city }),
                ...(form.country && { country: form.country }),
                ...(form.nationality && { nationality: form.nationality })
            }
        };

        const redirectTab = window.open('about:blank', '_blank');
        if (redirectTab) {
            redirectTab.opener = null;
            redirectTab.document.title = 'Preparing secure payment…';
            redirectTab.document.body.textContent = 'Preparing secure payment…';
        }

        try {
            setSubmitting(true);
            const result = await fetch(sandboxTransactionUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });
            setHttpStatus(result.status);
            const text = await result.text();
            let parsedResponse: unknown = null;
            try { parsedResponse = text ? JSON.parse(text) : null; } catch { parsedResponse = text; }
            setResponse(parsedResponse);

            const redirectUrl = findRedirectUrl(parsedResponse);
            if (result.ok && redirectUrl) {
                if (redirectTab) {
                    redirectTab.location.replace(redirectUrl);
                } else {
                    setError('The transaction was created, but the browser blocked the payment tab. Allow pop-ups and try again.');
                }
            } else {
                redirectTab?.close();
            }

            if (!result.ok) setError(`The sandbox returned HTTP ${result.status}. Review the response for details.`);
        } catch (requestError) {
            redirectTab?.close();
            setError(requestError instanceof Error ? requestError.message : 'The request could not be completed.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box component='section' id='transaction-playground' sx={{ scrollMarginTop: 104, py: 4 }}>
            <Typography variant='h4' gutterBottom>Try creating a transaction</Typography>
            <Typography color='text.secondary' paragraph>
                Send a test transaction without copying your API credentials. This playground always uses
                the sandbox <Box component='code'>testchannel</Box>.
            </Typography>
            <Alert severity={hasCredentials ? 'success' : 'warning'} sx={{ mb: 3 }}>
                {hasCredentials
                    ? 'API ID and API GUID were loaded from the signed-in merchant profile. The API GUID is never displayed.'
                    : 'API credentials are not available for this merchant profile.'}
            </Alert>
            {methodsError && <Alert severity='warning' sx={{ mb: 3 }}>{methodsError}</Alert>}

            <Box component='form' onSubmit={onSubmit}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                    {(Object.keys(form) as Array<keyof TransactionForm>)
                        .filter(field => !hiddenFields.includes(field))
                        .map(field => field === 'tag' ? (
                        <TextField key={field} select label={fieldLabels[field]} value={form[field]} required
                            disabled={visiblePaymentMethods.isLoading || !assignedMethodOptions.length}
                            helperText={visiblePaymentMethods.isLoading
                                ? 'Loading active payment methods…'
                                : (!assignedMethodOptions.length ? 'No active payment methods are displayed for this merchant.' : undefined)}
                            onChange={event => setPaymentMethod(event.target.value)} fullWidth>
                            {assignedMethodOptions.map(tag => (
                                <MenuItem key={tag} value={tag}>
                                    {tag}
                                </MenuItem>
                            ))}
                        </TextField>
                    ) : field === 'countrycode' ? (
                        <Autocomplete key={field}
                            options={selectedMethod?.countries ?? []}
                            value={form[field] || null}
                            disabled={!form.tag || areMethodDetailsLoading || !selectedMethod?.countries.length}
                            onChange={(_, value) => setField(field, value ?? '')}
                            autoHighlight
                            renderInput={params => (
                                <TextField {...params} label={fieldLabels[field]} required fullWidth
                                    helperText={areMethodDetailsLoading ? 'Loading supported countries…' : form.tag && !selectedMethod?.countries.length
                                        ? 'No countries are available for this tag.'
                                        : 'Type to search supported countries.'}
                                />
                            )}
                        />
                    ) : field === 'currency' ? (
                        <Autocomplete key={field}
                            options={selectedMethod?.currencies ?? []}
                            value={form[field] || null}
                            disabled={!form.tag || areMethodDetailsLoading || !selectedMethod?.currencies.length}
                            onChange={(_, value) => setField(field, value ?? '')}
                            autoHighlight
                            renderInput={params => (
                                <TextField {...params} label={fieldLabels[field]} required fullWidth
                                    helperText={areMethodDetailsLoading ? 'Loading supported currencies…' : form.tag && !selectedMethod?.currencies.length
                                        ? 'No currencies are available for this tag.'
                                        : 'Type to search supported currencies.'}
                                />
                            )}
                        />
                    ) : field === 'transaction_count' ? (
                        <TextField key={field} label={fieldLabels[field]} value={form[field]}
                            type='number' inputProps={{ min: 0, step: 1 }}
                            helperText='Optional. Enter the player transaction count known by the merchant.'
                            onChange={event => setField(field, event.target.value)} fullWidth />
                    ) : field === 'specin' ? (
                        <TextField key={field} label={fieldLabels[field]} value={form[field]}
                            helperText='Optional. The value is sent as plain text.'
                            onChange={event => setField(field, event.target.value)} fullWidth />
                    ) : (
                        <TextField key={field} label={fieldLabels[field]} value={form[field]}
                            required={requiredFields.includes(field)} type={field === 'email' ? 'email' : 'text'}
                            onChange={event => setField(field, event.target.value)} fullWidth />
                    ))}
                </Box>
                <Stack direction='row' spacing={2} alignItems='center' sx={{ mt: 3 }}>
                    <Button type='submit' variant='contained' disabled={!hasCredentials || isSubmitting}>
                        {isSubmitting ? 'Sending…' : 'Create test transaction'}
                    </Button>
                    {isSubmitting && <CircularProgress size={22} />}
                </Stack>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2, mt: 4 }}>
                <Paper variant='outlined' sx={{ overflow: 'hidden' }}>
                    <Typography variant='subtitle2' sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>Request preview</Typography>
                    <Box component='pre' sx={{ m: 0, p: 2, overflow: 'auto', fontSize: 12 }}>{JSON.stringify(requestPreview, null, 2)}</Box>
                </Paper>
                <Paper variant='outlined' sx={{ overflow: 'hidden' }}>
                    <Typography variant='subtitle2' sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
                        Response {httpStatus !== null ? `— HTTP ${httpStatus}` : ''}
                    </Typography>
                    <Box component='pre' sx={{ m: 0, p: 2, overflow: 'auto', fontSize: 12, minHeight: 160 }}>
                        {response === null ? 'Submit the form to see the sandbox response.' : JSON.stringify(response, null, 2)}
                    </Box>
                </Paper>
            </Box>
            {error && <Alert severity='error' sx={{ mt: 2 }}>{error}</Alert>}
        </Box>
    );
};

export default TransactionPlayground;
